import { Routes, Route } from 'react-router-dom'
import { lazy, Suspense } from 'react'
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin').then(m => ({ default: m.AdminLogin })))
const AdminSso = lazy(() => import('./pages/admin/AdminSso').then(m => ({ default: m.AdminSso })))
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })))
const AdminSubmissionDetail = lazy(() => import('./pages/admin/AdminSubmissionDetail').then(m => ({ default: m.AdminSubmissionDetail })))
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings').then(m => ({ default: m.AdminSettings })))
const AdminErrorLogs = lazy(() => import('./pages/admin/AdminErrorLogs').then(m => ({ default: m.AdminErrorLogs })))
const AdminPsychotherapy = lazy(() => import('./pages/admin/AdminPsychotherapy').then(m => ({ default: m.AdminPsychotherapy })))
const AdminFlowProgress = lazy(() => import('./pages/admin/AdminFlowProgress').then(m => ({ default: m.AdminFlowProgress })))
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics').then(m => ({ default: m.AdminAnalytics })))
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout').then(m => ({ default: m.AdminLayout })))
const SuperAdminLayout = lazy(() => import('./pages/superadmin/SuperAdminLayout').then(m => ({ default: m.SuperAdminLayout })))
const OrganizationList = lazy(() => import('./pages/superadmin/OrganizationList').then(m => ({ default: m.OrganizationList })))
const OrganizationDetail = lazy(() => import('./pages/superadmin/OrganizationDetail').then(m => ({ default: m.OrganizationDetail })))
const UserManagement = lazy(() => import('./pages/superadmin/UserManagement').then(m => ({ default: m.UserManagement })))
const GlobalSettings = lazy(() => import('./pages/superadmin/GlobalSettings').then(m => ({ default: m.GlobalSettings })))
const ApiTokens = lazy(() => import('./pages/superadmin/ApiTokens').then(m => ({ default: m.ApiTokens })))
// Admin text lives in its own i18n instance; lazy so patients never download it
const AdminI18nProvider = lazy(() => import('./components/AdminI18nProvider'))
import App from './App'
import { TenantProvider } from './context/TenantContext'
import { SepaSetupPage } from './pages/SepaSetupPage'
import { InformedConsentPage } from './pages/InformedConsentPage'
import { SignDocumentPage } from './pages/SignDocumentPage'
import { UploadDocumentsPage } from './pages/UploadDocumentsPage'
import { RelatedPersonPage } from './pages/RelatedPersonPage'
const AdminConsent = lazy(() => import('./pages/admin/AdminConsent').then(m => ({ default: m.AdminConsent })))
const AdminUploadLinks = lazy(() => import('./pages/admin/AdminUploadLinks').then(m => ({ default: m.AdminUploadLinks })))
const AdminGoya = lazy(() => import('./pages/admin/AdminGoya').then(m => ({ default: m.AdminGoya })))
const AdminRelatedPersons = lazy(() => import('./pages/admin/AdminRelatedPersons').then(m => ({ default: m.AdminRelatedPersons })))

export function AppRouter() {
  return (
    <TenantProvider>
      <Suspense fallback={<div className="min-h-screen bg-muted" />}>
      <Routes>
        {/* Super admin routes (no tenant scope) */}
        <Route path="/super-admin" element={<AdminI18nProvider><SuperAdminLayout /></AdminI18nProvider>}>
          <Route index element={<OrganizationList />} />
          <Route path="organizations" element={<OrganizationList />} />
          <Route path="organizations/:id" element={<OrganizationDetail />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="settings" element={<GlobalSettings />} />

          <Route path="api-tokens" element={<ApiTokens />} />
        </Route>

        {/* Org-scoped admin routes */}
        <Route path="/admin/login" element={<AdminI18nProvider><AdminLogin /></AdminI18nProvider>} />
        <Route path="/admin/sso" element={<AdminI18nProvider><AdminSso /></AdminI18nProvider>} />
        <Route path="/admin" element={<AdminI18nProvider><AdminLayout /></AdminI18nProvider>}>
          <Route index element={<AdminDashboard />} />
          <Route path="submissions/:patientNumber" element={<AdminSubmissionDetail />} />
          <Route path="error-logs" element={<AdminErrorLogs />} />
          <Route path="flow-progress" element={<AdminFlowProgress />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="consent" element={<AdminConsent />} />
          <Route path="upload-links" element={<AdminUploadLinks />} />
          <Route path="related-persons" element={<AdminRelatedPersons />} />
          <Route path="psychotherapy" element={<AdminPsychotherapy />} />
          <Route path="goya" element={<AdminGoya />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        {/* Standalone SEPA mandate setup (direct link, outside the questionnaire) */}
        <Route path="/sepa" element={<SepaSetupPage />} />

        {/* Standalone informed-consent signing (link from the practice / Make) */}
        <Route path="/informed-consent" element={<InformedConsentPage />} />

        {/* Standalone signing of an externally produced document (e.g. a clinician signing DRV forms) */}
        <Route path="/sign" element={<SignDocumentPage />} />

        {/* Standalone document upload into the patient's file (per-patient link sent by staff) */}
        <Route path="/upload" element={<UploadDocumentsPage />} />
        {/* Bezugspersonenbogen: the patient names Bezugspersonen (per-patient link, docs/bezugspersonenbogen.md) */}
        <Route path="/bezugsperson" element={<RelatedPersonPage />} />

        {/* Patient form (catch-all) */}
        <Route path="*" element={<App />} />
      </Routes>
      </Suspense>
    </TenantProvider>
  )
}
